<div class="flex flex-col gap-6 animate-in fade-in duration-500">

    <!-- Filters -->
    <div class="flex flex-col xl:flex-row xl:items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
        <div class="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            <!-- Options come from available_doctors, which the RPC computes
                 before applying the filter — so narrowing the selection never
                 removes the entries needed to widen it again. -->
            <div class="w-full sm:w-72 lg:w-80">
                <p-multiSelect
                    [options]="availableDoctors"
                    [(ngModel)]="selectedDoctorIds"
                    optionLabel="doctor_name"
                    optionValue="doctor_id"
                    [filter]="true"
                    filterBy="doctor_name"
                    [showToggleAll]="true"
                    display="chip"
                    [maxSelectedLabels]="2"
                    [selectedItemsLabel]="'WORKLOAD_N_SELECTED' | translate"
                    [placeholder]="'WORKLOAD_ALL_PROFESSIONALS' | translate"
                    [emptyMessage]="'WORKLOAD_NO_DATA' | translate"
                    [emptyFilterMessage]="'WORKLOAD_NO_MATCH' | translate"
                    (onChange)="onDoctorSelectionChange()"
                    appendTo="body"
                    styleClass="w-full !min-h-[44px] !rounded-xl !border-slate-200"
                    class="w-full">
                    <ng-template let-doctor #item>
                        <div class="flex items-center justify-between gap-3 w-full">
                            <span class="truncate">{{ doctor.doctor_name }}</span>
                            <span class="text-[10px] tabular-nums text-slate-400 shrink-0">
                                {{ doctor.appt_count }}
                            </span>
                        </div>
                    </ng-template>
                </p-multiSelect>
            </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 xl:justify-end shrink-0">
            <div class="flex items-center gap-2">
                <div class="w-32 lg:w-36">
                    <p-datepicker
                        [(ngModel)]="startDate"
                        [placeholder]="'START_DATE' | translate"
                        [showIcon]="true"
                        [maxDate]="maxDate"
                        iconDisplay="input"
                        appendTo="body"
                        inputStyleClass="w-full !h-[44px] !border-slate-200 !rounded-xl !text-sm focus:!border-primary focus:!shadow-none"
                        styleClass="w-full">
                    </p-datepicker>
                </div>
                <div class="w-32 lg:w-36">
                    <p-datepicker
                        [(ngModel)]="endDate"
                        [placeholder]="'END_DATE' | translate"
                        [showIcon]="true"
                        [maxDate]="maxDate"
                        iconDisplay="input"
                        appendTo="body"
                        inputStyleClass="w-full !h-[44px] !border-slate-200 !rounded-xl !text-sm focus:!border-primary focus:!shadow-none"
                        styleClass="w-full">
                    </p-datepicker>
                </div>
            </div>

            <div class="flex items-center gap-2">
                <app-button
                    [label]="'today_filter' | translate"
                    icon="pi pi-calendar"
                    (onClick)="filterToday()"
                    variant="secondary"
                    class="shrink-0">
                </app-button>

                <app-button
                    [label]="'apply_filter' | translate"
                    icon="pi pi-filter"
                    (onClick)="applyFilters()"
                    class="shrink-0">
                </app-button>

                <p-button
                    *ngIf="hasCustomFilters"
                    icon="pi pi-filter-slash"
                    [rounded]="true"
                    severity="secondary"
                    variant="text"
                    (onClick)="clearFilters()"
                    [pTooltip]="'clear_filters' | translate"
                    tooltipPosition="bottom"
                    styleClass="!h-[44px] !w-[44px] !text-slate-400 hover:!bg-slate-50">
                </p-button>
            </div>
        </div>
    </div>

    <!-- An estimate presented as a measurement is worse than no number, so say
         so whenever any day fell back off a real roster. -->
    <div *ngIf="usesEstimatedHours && !loading"
        class="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100 text-amber-700">
        <i class="pi pi-info-circle mt-0.5"></i>
        <span class="text-xs font-medium">
            {{ 'WORKLOAD_ESTIMATED_HOURS' | translate:{ count: estimatedDayCount } }}
        </span>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="flex items-center justify-center py-24 bg-white rounded-2xl border border-slate-100">
        <i class="pi pi-spin pi-spinner text-3xl text-slate-300"></i>
    </div>

    <ng-container *ngIf="!loading && report">

        <!-- States the range the figures actually cover. The RPC resolves blanks
             and caps the end at today, so naming it here means a resolved range
             can never quietly differ from what the pickers show. -->
        <div *ngIf="rangeLabel" class="flex items-center gap-2 -mb-2 text-xs text-slate-400">
            <i class="pi pi-calendar text-[11px]"></i>
            <span>{{ 'WORKLOAD_SHOWING' | translate }} <span class="font-semibold text-slate-600">{{ rangeLabel }}</span></span>
        </div>

        <!-- Headline figures -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div class="p-5 bg-white rounded-2xl shadow-sm border border-slate-100">
                <div class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {{ 'WORKLOAD_TOTAL_BOOKED' | translate }}
                </div>
                <div class="mt-2 text-3xl font-black text-slate-800 tabular-nums">
                    {{ formatHours(report.totals.booked_hours) }}
                </div>
                <div class="mt-1 text-xs text-slate-400">
                    {{ report.totals.appts }} {{ 'WORKLOAD_APPOINTMENTS' | translate }}
                    <span *ngIf="report.totals.noshows > 0">
                        · {{ report.totals.noshows }} {{ 'WORKLOAD_NOSHOWS' | translate }}
                    </span>
                </div>
            </div>

            <div class="p-5 bg-white rounded-2xl shadow-sm border border-slate-100">
                <div class="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {{ 'WORKLOAD_AVG' | translate }}
                </div>
                <div class="mt-2 text-3xl font-black text-slate-800 tabular-nums">
                    {{ formatPercentage(report.totals.workload_percentage) }}
                </div>
                <div class="mt-1 text-xs text-slate-400">
                    {{ report.totals.doctors }} {{ 'WORKLOAD_PROFESSIONALS' | translate }}
                </div>
            </div>

            <!-- Impossible schedules: one person booked into two places at once.
                 The most actionable number on the page. -->
            <div class="p-5 bg-white rounded-2xl shadow-sm border"
                 [ngClass]="report.totals.overlap_hours > 0 ? 'border-rose-200' : 'border-slate-100'">
                <div class="text-[11px] font-bold uppercase tracking-wider"
                     [ngClass]="report.totals.overlap_hours > 0 ? 'text-rose-500' : 'text-slate-400'">
                    {{ 'WORKLOAD_DOUBLE_BOOKED' | translate }}
                </div>
                <div class="mt-2 text-3xl font-black tabular-nums"
                     [ngClass]="report.totals.overlap_hours > 0 ? 'text-rose-600' : 'text-slate-800'">
                    {{ formatHours(report.totals.overlap_hours) }}
                </div>
                <div class="mt-1 text-xs text-slate-400">
                    {{ doubleBookedDoctors.length }} {{ 'WORKLOAD_PROFESSIONALS_AFFECTED' | translate }}
                </div>
            </div>

            <div class="p-5 bg-white rounded-2xl shadow-sm border"
                 [ngClass]="report.totals.rooms_over_capacity > 0 ? 'border-rose-200' : 'border-slate-100'">
                <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="text-[11px] font-bold uppercase tracking-wider"
                          [ngClass]="report.totals.rooms_over_capacity > 0 ? 'text-rose-500' : 'text-slate-400'">
                        {{ 'WORKLOAD_ROOMS_OVER_CAPACITY' | translate }}
                    </span>
                    <!-- Capacity counts every patient in the room, so it cannot be
                         scoped to one professional. Say that, rather than letting
                         this tile look filtered like its three neighbours. -->
                    <span *ngIf="isProfessionalFiltered"
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500"
                          [pTooltip]="'WORKLOAD_CLINIC_WIDE_HINT' | translate"
                          tooltipPosition="top"
                          tooltipStyleClass="wrap-tooltip"
                          appendTo="body">
                        {{ 'WORKLOAD_CLINIC_WIDE' | translate }}
                    </span>
                </div>
                <div class="mt-2 text-3xl font-black tabular-nums"
                     [ngClass]="report.totals.rooms_over_capacity > 0 ? 'text-rose-600' : 'text-slate-800'">
                    {{ report.totals.rooms_over_capacity }}
                </div>
                <div class="mt-1 text-xs text-slate-400">
                    {{ 'WORKLOAD_OF_N_ROOMS' | translate:{ count: rooms.length } }}
                </div>
            </div>
        </div>

        <!-- Workload per professional -->
        <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div class="flex items-baseline justify-between gap-4 flex-wrap mb-1">
                <h3 class="text-sm font-bold text-slate-700">{{ 'WORKLOAD_BY_PROFESSIONAL' | translate }}</h3>
                <span class="text-xs text-slate-400">{{ 'WORKLOAD_BY_PROFESSIONAL_HINT' | translate }}</span>
            </div>

            <div *ngIf="chartData" class="w-full" [style.height.px]="chartHeight">
                <p-chart type="bar" [data]="chartData" [options]="chartOptions" height="100%"></p-chart>
            </div>

            <div *ngIf="!chartData" class="py-12 text-center text-sm text-slate-400">
                {{ 'WORKLOAD_NO_DATA' | translate }}
            </div>

            <!-- Colour alone never carries the meaning. -->
            <div class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pt-4 border-t border-slate-100">
                <span *ngFor="let band of bands" class="inline-flex items-center gap-2 text-xs text-slate-500">
                    <span class="w-3 h-3 rounded-sm" [style.background-color]="band.hex"></span>
                    {{ band.key | translate }}
                </span>
            </div>
        </div>

        <!-- Daily trend. The heatmap says how heavy each day was; this says
             whether somebody is climbing while somebody else is flat, which
             only a shared y-axis can show. -->
        <div *ngIf="trendData || trendTooManySeries"
             class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div class="flex items-baseline justify-between gap-4 flex-wrap mb-2">
                <h3 class="text-sm font-bold text-slate-700">{{ 'WORKLOAD_TREND' | translate }}</h3>
                <span class="text-xs text-slate-400">{{ 'WORKLOAD_TREND_HINT' | translate }}</span>
            </div>

            <div *ngIf="trendData" class="w-full h-[320px]">
                <p-chart type="line" [data]="trendData" [options]="trendOptions" height="100%"></p-chart>
            </div>

            <!-- Above the cap the lines stop being separable, so the chart folds
                 away rather than drawing something unreadable. -->
            <div *ngIf="trendTooManySeries"
                 class="flex items-start gap-2 px-4 py-3 rounded-xl bg-slate-50 text-slate-500">
                <i class="pi pi-chart-line mt-0.5"></i>
                <span class="text-xs font-medium">
                    {{ 'WORKLOAD_TREND_TOO_MANY' | translate:{ count: trendSeriesCap } }}
                </span>
            </div>
        </div>

        <!-- Day-by-day heatmap -->
        <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div class="flex items-baseline justify-between gap-4 flex-wrap mb-4">
                <h3 class="text-sm font-bold text-slate-700">{{ 'WORKLOAD_HEATMAP' | translate }}</h3>
                <span class="text-xs text-slate-400">{{ 'WORKLOAD_HEATMAP_HINT' | translate }}</span>
            </div>

            <!-- CSS grid rather than a table so the day columns stretch to fill
                 the card. Each column is minmax(22px, 1fr): it grows into spare
                 width on a short range, and once a long range pushes the total
                 past the container the 22px floor takes over and this scrolls
                 instead of shrinking the cells into illegibility. -->
            <div *ngIf="heatRows.length" class="overflow-x-auto -mx-1 px-1">
                <div class="grid items-center gap-[2px]" [style.grid-template-columns]="heatGridColumns">

                    <!-- Header: sticky spacer, then one label per day -->
                    <div class="sticky left-0 z-10 bg-white h-full"></div>
                    <div *ngFor="let d of dayAxis"
                         class="text-center text-[9px] font-semibold leading-tight pb-1"
                         [ngClass]="d.isWeekend ? 'text-slate-300' : 'text-slate-400'">
                        <div>{{ d.dow }}</div>
                        <div>{{ d.label }}</div>
                    </div>

                    <ng-container *ngFor="let row of heatRows; trackBy: trackByDoctor">
                        <div class="sticky left-0 z-10 bg-white pr-3 whitespace-nowrap text-xs font-medium text-slate-600 truncate"
                             [title]="row.doctor_name">
                            <span class="inline-flex items-center gap-1.5">
                                <!-- Same slot as the trend line above, so a
                                     professional keeps one identity colour
                                     across every view on the page. -->
                                <span *ngIf="seriesColour(row.doctor_id) as dot"
                                      class="w-2 h-2 rounded-full shrink-0"
                                      [style.background-color]="dot"></span>
                                <span class="truncate">{{ row.doctor_name }}</span>
                            </span>
                        </div>

                        <!-- The overlap ring is a second encoding: a double-booked
                             day must not be mistakable for a merely busy one. -->
                        <!-- appendTo="body" keeps the tooltip out of this
                             overflow-x-auto wrapper, which would otherwise clip
                             it against the scroll box. wrap-tooltip is the
                             app-wide class for long text: the default tooltip
                             style in styles.css is nowrap + uppercase, which
                             turns a sentence into one giant strip. -->
                        <div *ngFor="let cell of row.cells; trackBy: trackByCell"
                             class="h-8 rounded-[3px] relative"
                             [ngStyle]="cellStyle(cell)"
                             [pTooltip]="cellTooltip(row, cell)"
                             tooltipPosition="top"
                             tooltipStyleClass="wrap-tooltip"
                             appendTo="body"
                             [showDelay]="120">
                            <span *ngIf="cell.overlapHours > 0"
                                  class="absolute inset-0 rounded-[3px] ring-2 ring-inset ring-rose-600"></span>
                        </div>
                    </ng-container>
                </div>
            </div>

            <div *ngIf="!heatRows.length" class="py-12 text-center text-sm text-slate-400">
                {{ 'WORKLOAD_NO_DATA' | translate }}
            </div>

            <!-- The bands are repeated here rather than only on the chart above:
                 this is where the colours are actually read. -->
            <div *ngIf="heatRows.length" class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pt-4 border-t border-slate-100">
                <span *ngFor="let band of bands" class="inline-flex items-center gap-2 text-xs text-slate-500">
                    <span class="w-3 h-3 rounded-sm" [style.background-color]="band.hex"></span>
                    {{ band.key | translate }}
                </span>
                <span class="inline-flex items-center gap-2 text-xs text-slate-500">
                    <span class="w-3 h-3 rounded-sm bg-slate-100"></span>
                    {{ 'WORKLOAD_NO_APPOINTMENTS' | translate }}
                </span>
                <span class="inline-flex items-center gap-2 text-xs text-slate-500">
                    <span class="w-3 h-3 rounded-sm ring-2 ring-inset ring-rose-600"></span>
                    {{ 'WORKLOAD_DOUBLE_BOOKED' | translate }}
                </span>
            </div>
        </div>

        <!-- Per-professional detail -->
        <div class="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div class="p-5 pb-3">
                <h3 class="text-sm font-bold text-slate-700">{{ 'WORKLOAD_DETAIL' | translate }}</h3>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full text-sm">
                    <thead>
                        <tr class="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                            <th class="text-left font-bold px-5 py-3">{{ 'WORKLOAD_PROFESSIONAL' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_DAYS' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_APPOINTMENTS' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_AVG_PER_DAY' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_PEAK_DAY' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'BOOKED_HOURS' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_DOUBLE_BOOKED' | translate }}</th>
                            <th class="text-right font-bold px-4 py-3">{{ 'WORKLOAD_PERCENT' | translate }}</th>
                            <th class="text-left font-bold px-5 py-3">{{ 'WORKLOAD_BASIS' | translate }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let d of doctors" class="border-t border-slate-50 hover:bg-slate-50/50 transition-colors">
                            <td class="px-5 py-3 font-medium text-slate-700 whitespace-nowrap">
                                <span class="inline-flex items-center gap-2">
                                    <span *ngIf="seriesColour(d.doctor_id) as dot"
                                          class="w-2 h-2 rounded-full shrink-0"
                                          [style.background-color]="dot"></span>
                                    {{ d.doctor_name }}
                                </span>
                            </td>
                            <td class="px-4 py-3 text-right tabular-nums text-slate-500">{{ d.days_worked }}</td>
                            <td class="px-4 py-3 text-right tabular-nums text-slate-500">
                                {{ d.appt_count }}
                                <span *ngIf="d.noshow_count > 0" class="text-slate-300">({{ d.noshow_count }})</span>
                            </td>
                            <td class="px-4 py-3 text-right tabular-nums text-slate-500">{{ d.avg_appts_day }}</td>
                            <td class="px-4 py-3 text-right tabular-nums text-slate-500">{{ d.peak_appts_day }}</td>
                            <td class="px-4 py-3 text-right tabular-nums font-semibold text-slate-800">{{ formatHours(d.booked_hours) }}</td>
                            <td class="px-4 py-3 text-right tabular-nums"
                                [ngClass]="d.overlap_hours > 0 ? 'text-rose-600 font-semibold' : 'text-slate-300'">
                                {{ d.overlap_hours > 0 ? formatHours(d.overlap_hours) : '—' }}
                            </td>
                            <td class="px-4 py-3 text-right">
                                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                                      [ngClass]="workloadClass(d.workload_percentage)">
                                    {{ formatPercentage(d.workload_percentage) }}
                                </span>
                            </td>
                            <td class="px-5 py-3">
                                <span class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold whitespace-nowrap"
                                      [ngClass]="sourceClass(d.workload_source)">
                                    {{ sourceLabel(d.workload_source) }}
                                </span>
                            </td>
                        </tr>
                        <tr *ngIf="!doctors.length">
                            <td colspan="9" class="px-5 py-12 text-center text-sm text-slate-400">
                                {{ 'WORKLOAD_NO_DATA' | translate }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Room capacity: a ceiling test, never a divisor. Dividing booked time
             by capacity makes a 50-chair room read as permanently empty. -->
        <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <div class="flex items-baseline justify-between gap-4 flex-wrap mb-4">
                <div class="flex items-center gap-2 flex-wrap">
                    <h3 class="text-sm font-bold text-slate-700">{{ 'WORKLOAD_ROOM_PRESSURE' | translate }}</h3>
                    <span *ngIf="isProfessionalFiltered"
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
                        {{ 'WORKLOAD_CLINIC_WIDE' | translate }}
                    </span>
                </div>
                <span class="text-xs text-slate-400">
                    {{ (isProfessionalFiltered ? 'WORKLOAD_CLINIC_WIDE_HINT' : 'WORKLOAD_ROOM_PRESSURE_HINT') | translate }}
                </span>
            </div>

            <div *ngIf="rooms.length" class="flex flex-col gap-3">
                <div *ngFor="let room of rooms; trackBy: trackByRoom"
                     class="flex items-center gap-4 flex-wrap sm:flex-nowrap">
                    <div class="w-40 shrink-0 text-sm font-medium text-slate-700 truncate" [title]="room.room_name">
                        {{ room.room_name }}
                    </div>

                    <div class="flex-1 min-w-[120px] h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div class="h-full rounded-full transition-all"
                             [ngClass]="capacityBarClass(room)"
                             [style.width.%]="capacityRatio(room)"></div>
                    </div>

                    <div class="w-28 shrink-0 text-right text-xs tabular-nums text-slate-500">
                        {{ room.peak_concurrency }} / {{ room.capacity }}
                        <span class="text-slate-300">{{ 'WORKLOAD_CHAIRS' | translate }}</span>
                    </div>

                    <div class="w-32 shrink-0 text-right">
                        <span *ngIf="room.days_over_capacity > 0"
                              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-50 text-rose-600">
                            <i class="pi pi-exclamation-triangle text-[9px]"></i>
                            {{ 'WORKLOAD_OVER_ON_N_DAYS' | translate:{ count: room.days_over_capacity } }}
                        </span>
                        <span *ngIf="room.days_over_capacity === 0" class="text-[10px] text-slate-300 uppercase tracking-wider font-bold">
                            {{ 'WORKLOAD_WITHIN_CAPACITY' | translate }}
                        </span>
                    </div>
                </div>
            </div>

            <div *ngIf="!rooms.length" class="py-12 text-center text-sm text-slate-400">
                {{ 'WORKLOAD_NO_DATA' | translate }}
            </div>
        </div>

    </ng-container>
</div>
